import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import {
  ErrorCode,
  PreflightCopy,
  neverUploadedToStateBody,
  notOnFilingBody,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import {
  useMarkFiledInPortal,
  useMarkFiledPreview,
  MarkFiledInPortalError,
} from '@/hooks/queries/useFinanceReports';
import { receivingOfficePhrase } from './receivingOffice';
import { campaignLocalInputToUtcIso, campaignTimeZoneId } from '@/lib/campaignDateTimeInput';
import { useCampaignStore } from '@/stores';
import { answered, reportsUnreadyMessage } from '@/lib/reportsRead';

interface MarkFiledInPortalButtonProps {
  report: ComplianceReportDetailDto;
  /**
   * Campaign's resolved state code (the sole jurisdiction source of truth).
   * Drives the registered-format lookup; null = not configured.
   */
  stateCode: string | null;
  /**
   * #482 — federal download-and-self-upload: the treasurer uploads the `.fec` to efo.fec.gov and
   * the FEC returns a filing id. When true this confirm dialog captures that id (recorded as the
   * State Filing Receipt) as part of marking filed, and the copy is worded around the FEC. False
   * for the portal-upload jurisdictions (AL/VA/NC/LA), whose flow is unchanged — they record the
   * portal receipt separately via the receipt editor once Filed.
   */
  certifyAtGenerate?: boolean;
  /**
   * The jurisdiction's receipt is the proof of filing (`profile.filingReceipt === 'proofOfFiling'` —
   * Mississippi): the confirm dialog offers the receiving office's confirmation of receipt — its
   * identifier and when it was received — as an optional atomic pair. Blank is fine (a treasurer who
   * hasn't heard back is never blocked); the report then carries the receipt chase until it's recorded.
   */
  captureStateReceipt?: boolean;
  /**
   * Receives the "Recorded N transactions as filed." confirmation. The parent unmounts this button
   * as soon as the refetched report reads Filed, so a confirmation shown only here would vanish.
   */
  onRecorded?: (message: string) => void;
}

function pluralTx(n: number): string {
  return n === 1 ? '1 transaction' : `${n} transactions`;
}

/**
 * "Mark filed in portal" (ADR-023) — the treasurer attests the period report was filed in
 * the portal. This is now a COMMITTING attestation: because upload ≠ file, uploading the
 * EDI batch does not finalize the report; this report-level attestation does. It flips the
 * report's period `Confirmed` rows `Confirmed → Reported` in the same action that sets
 * Status = Filed + FilingOrigin = PortalAttested, and is reversible via Reopen.
 *
 * Because it always mutates transactions now, it ALWAYS confirms (ADR-023): the dialog
 * carries the append-only trust contract and the pre-confirm count from
 * `GET mark-filed-preview`. Jurisdiction names come from `usePortalUploadModel` — never a
 * hard-coded "Alabama" / "FCPA portal".
 *
 * Rendered for every upload-≠-filing jurisdiction (serving model `uploadIsFiling = false`) — every
 * jurisdiction today: AL's append-only delta, VA's whole-report COMET upload, NC's email-to-NCSBE
 * (#296), and federal's download-and-self-upload to efo.fec.gov (#482) — and only for PERIOD reports
 * (a real deadlineId); rapid / major-contribution reports finalize from their own filing card. The
 * copy is worded per transport: NC around emailing ("Confirm filed" / "Yes, I emailed it to NCSBE"),
 * and — when `certifyAtGenerate` (federal) — around the FEC, additionally capturing the FEC filing id
 * the treasurer got back from efo.fec.gov as the confirmation number. Once the report is Filed via
 * Portal- or EmailAttested the parent hides this and shows Reopen instead (idempotency: the only
 * forward move is Reopen).
 */
function recordedMessage(count: number, notOnFiling: number): string {
  const recorded = count === 0 ? 'Marked as filed.' : `Recorded ${pluralTx(count)} as filed.`;
  return notOnFiling > 0 ? `${recorded} ${notOnFilingBody(notOnFiling)}` : recorded;
}

export function MarkFiledInPortalButton({
  report,
  stateCode,
  certifyAtGenerate = false,
  captureStateReceipt = false,
  onRecorded,
}: MarkFiledInPortalButtonProps) {
  // Jurisdiction + serving-model seam — state/portal names, the render gate, AND the filing
  // TRANSPORT all come from the resolved portal-upload model (backed by the served #478 descriptor).
  // `isPortalUpload` (upload ≠ file) is true for every jurisdiction now (AL, VA, NC, and federal —
  // #482), so this renders without a state-named branch. #481 — `isEmailFiling` is the served
  // transport axis (`profile.filingTransport === 'email'`), replacing the old `stateCode === 'NC'`
  // check so a future email-filing state gets email-worded copy without a hand edit.
  const { isPortalUpload, isEmailFiling, stateDisplayName, portalName, rulesetCode } =
    usePortalUploadModel();
  const { markFiledInPortal } = useMarkFiledInPortal();

  // The server refuses mark-filed below Finance Manage, so a view-only user gets no control (and no
  // preview fetch). Period reports carry a real deadline; rapid / major-contribution reports do not.
  const canManage = useCampaignStore((st) => st.canManage('Finance'));
  const enabled = canManage && !!stateCode && isPortalUpload && !!report.deadlineId;

  // Pre-confirm count for the always-confirm dialog (fetched only while the action shows).
  const previewRead = useMarkFiledPreview(enabled ? report.id : null);
  const preview = answered(previewRead);
  // The confirm states how many rows it records; without the count it says so rather than "0".
  const previewUnready = reportsUnreadyMessage('count of transactions this records', previewRead);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recorded, setRecorded] = useState<string | null>(null);
  // #482 — the FEC filing id the treasurer got back from efo.fec.gov, recorded as part of marking
  // filed (federal only; portal jurisdictions record their receipt separately once Filed).
  const [fecFilingId, setFecFilingId] = useState('');
  // The receiving office's confirmation of receipt (captureStateReceipt only) — an atomic pair.
  const [receiptId, setReceiptId] = useState('');
  const [receiptAt, setReceiptAt] = useState('');

  if (!enabled) {
    if (!stateCode || !isPortalUpload) return null;
    return (
      <p className="text-sm text-muted-foreground" data-testid="mark-filed-unavailable-note">
        {!canManage
          ? "Marking this report filed needs Finance manage access. Ask a campaign admin to do it."
          : "This report isn't tied to a filing period. Mark it filed from its own filing card."}
      </p>
    );
  }

  const count = preview?.transactionsToRecord ?? 0;
  const counted = (n: number) => (previewUnready ? "this period's confirmed transactions" : pluralTx(n));
  const office = receivingOfficePhrase(portalName);
  const trimmedReceiptId = receiptId.trim();
  const receiptHalfPair = captureStateReceipt && (trimmedReceiptId === '') !== (receiptAt === '');
  // #646 — the omission nudge: how many of the rows this attestation records as filed were never
  // uploaded to the state (server-derived from the #642 already-sent check; 0 for non-Incremental /
  // RemindOnly). A SOFT alert — it informs the treasurer inside the confirm, it never blocks marking
  // filed. Shown only when > 0.
  const neverUploaded = preview?.neverUploadedCount ?? 0;
  // Rows entered after the generate: the document the treasurer uploaded never carried them, so
  // marking filed leaves them Confirmed and the treasurer amends to add them. Also a soft alert.
  const notOnFiling = preview?.notOnFilingCount ?? 0;
  const jurisdiction = stateDisplayName ? `${stateDisplayName} ${portalName}` : portalName;
  // ADR-023 — the always-confirm body carries the same append-only trust contract as the
  // export gate's "Yes, it imported": marking filed flips rows Reported ("won't re-export"),
  // so attesting without having filed in the portal silently under-reports.
  //
  // Transport-routed confirm copy (#481). The email vs portal fork is the SERVED transport axis
  // (`isEmailFiling`), never a raw `stateCode === 'NC'` check:
  // #296/#860 — an EMAIL-transport jurisdiction (no portal, no confirmation number) gets the "confirm
  //   you EMAILED it" copy — never "in the portal". The body + accept label now compose the SERVED
  //   receiving-office name (`portalName`) via the `{office}` token instead of a hardcoded board, so a
  //   second email-transport jurisdiction reads its own office (NC → "NCSBE"; Mississippi → "the
  //   Secretary of State" / "the Circuit Clerk") with no component edit. NC renders identically
  //   (its served portalName is "NCSBE").
  // Issue #186 — Louisiana (LEADERS) is a within-PORTAL jurisdiction nuance: same `portal` transport
  //   as AL/VA, but a distinct "confirm you UPLOADED" trust contract (never "submitted"/"attested").
  //   The transport axis models portal/email/inSystem, not this wording nuance, so LA stays an
  //   explicit override keyed on the SERVED ruleset code (`rulesetCode`) — not a re-derived acronym.
  //   AL/VA keep the derived state+portal-name body.
  const isLouisiana = rulesetCode?.toUpperCase() === 'LA';
  const confirmBody = certifyAtGenerate
    ? `This records ${counted(count)} as filed with the FEC — they'll show reported and won't be `
      + `re-filed on the next report. Only do this once you've uploaded the .fec at efo.fec.gov.`
    : isEmailFiling
      ? PreflightCopy.EmailMarkFiledConfirmBody.replace('{N}', counted(count)).replace('{office}', office)
      : isLouisiana
        ? PreflightCopy.LouisianaMarkFiledConfirmBody.replace('{N}', counted(count))
        : `This records ${counted(count)} as filed in the ${jurisdiction} — they'll show reported `
          + `and won't be re-exported. Only do this if you actually filed them in the portal.`;

  const handleMarkFiled = async () => {
    setBusy(true);
    setError(null);
    setRecorded(null);
    try {
      const result = await markFiledInPortal({
        reportId: report.id,
        data: {
          concurrencyVersion: report.concurrencyVersion,
          // Record the FEC filing id captured above (federal); portal jurisdictions send none here
          // and record their receipt separately. Trimmed-empty → omitted (the field is optional).
          confirmationNumber: certifyAtGenerate && fecFilingId.trim()
            ? fecFilingId.trim()
            : captureStateReceipt && trimmedReceiptId ? trimmedReceiptId : null,
          // The office's receipt timestamp — entered as CAMPAIGN wall-clock time, sent as UTC, and only
          // with its identifier.
          receiptAt: captureStateReceipt && trimmedReceiptId && receiptAt
            ? campaignLocalInputToUtcIso(receiptAt, campaignTimeZoneId())
            : null,
        },
      });
      const message = recordedMessage(result.flippedCount, result.notOnFilingCount);
      setRecorded(message);
      onRecorded?.(message);
    } catch (e) {
      if (e instanceof MarkFiledInPortalError) {
        // A live Generated export draft must be resolved first (mirrors the export gate) —
        // flipping rows out-of-band would orphan the draft.
        if (e.code === ErrorCode.MarkFiledPendingExportMustResolveFirst) {
          setError(
            'You have an export waiting to be confirmed — tell us whether the portal '
            + 'accepted it first.',
          );
          return;
        }
        // Should be unreachable (the parent shows Reopen once filed), but surface it
        // gracefully rather than as a raw error if the state races.
        if (e.code === ErrorCode.MarkFiledAlreadyFiled) {
          setError('This report is already filed. Refresh to see its current status.');
          return;
        }
      }
      setError(e instanceof Error ? e.message : 'Failed to mark the report filed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        onClick={() => {
          setError(null);
          setRecorded(null);
          setConfirmOpen(true);
        }}
        disabled={busy}
        data-testid="mark-filed-in-portal-button"
      >
        <CheckCircle2 className="mr-1 h-4 w-4" />
        {busy
          ? (isEmailFiling ? 'Confirming…' : 'Marking as filed…')
          : certifyAtGenerate
            ? 'Mark filed with FEC'
            : (isEmailFiling ? 'Confirm filed' : 'Mark filed in portal')}
      </Button>

      {/* Always-confirm (ADR-023): this commits transactions, so it never fires on the
          first click. On confirm we proceed to the committing attestation. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Record this report as filed?</AlertDialogTitle>
            <AlertDialogDescription data-testid="mark-filed-confirm-body">
              {confirmBody}
            </AlertDialogDescription>
            {/* #646 — soft omission nudge (never blocks): some rows about to be recorded as filed
                were never uploaded to the state. */}
            {previewUnready && (
              <p
                className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="mark-filed-preview-unready"
              >
                {previewUnready}
              </p>
            )}
            {neverUploaded > 0 && (
              <p
                className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="mark-filed-never-uploaded-warning"
              >
                {neverUploadedToStateBody(neverUploaded)}
              </p>
            )}
            {notOnFiling > 0 && (
              <p
                className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="mark-filed-not-on-filing-warning"
              >
                {notOnFilingBody(notOnFiling)}
              </p>
            )}
          </AlertDialogHeader>
          {/* #482 — capture the FEC filing id the treasurer got back from efo.fec.gov as part of
              marking filed (federal only). Optional — a treasurer who doesn't have it yet can add it
              later via the receipt editor once Filed. */}
          {certifyAtGenerate && (
            <div className="space-y-1">
              <label htmlFor="fec-filing-id" className="text-sm font-medium">
                FEC filing id <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <input
                id="fec-filing-id"
                type="text"
                value={fecFilingId}
                onChange={(e) => setFecFilingId(e.target.value)}
                placeholder="e.g. FEC-1874321"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="mark-filed-fec-filing-id"
              />
            </div>
          )}
          {captureStateReceipt && (
            <div className="space-y-2" data-testid="mark-filed-receipt-capture">
              <p className="text-sm">
                Got {office}&apos;s confirmation of receipt?{' '}
                <span className="text-muted-foreground">
                  (optional — you can add it later; it&apos;s your proof the report arrived on time)
                </span>
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  aria-label="Confirmation identifier"
                  type="text"
                  value={receiptId}
                  onChange={(e) => setReceiptId(e.target.value)}
                  placeholder="Confirmation identifier"
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="mark-filed-receipt-id"
                />
                <input
                  aria-label="Received at"
                  type="datetime-local"
                  value={receiptAt}
                  onChange={(e) => setReceiptAt(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="mark-filed-receipt-at"
                />
              </div>
              {receiptHalfPair && (
                <p className="text-sm text-amber-800" data-testid="mark-filed-receipt-half-pair">
                  Enter both the identifier and when it was received — or leave both blank.
                </p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-filed-confirm-cancel">Go back</AlertDialogCancel>
            <AlertDialogAction
              data-testid="mark-filed-confirm-accept"
              disabled={receiptHalfPair}
              onClick={() => {
                setConfirmOpen(false);
                void handleMarkFiled();
              }}
            >
              {certifyAtGenerate
                ? 'Yes, I filed it with the FEC'
                : isEmailFiling
                  ? `Yes, I emailed it to ${office}`
                  : 'Yes, I filed it in the portal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {recorded !== null && (
        <p className="text-sm text-green-700" data-testid="mark-filed-success">
          {recorded}
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="mark-filed-error">
          {error}
        </p>
      )}
    </div>
  );
}
